import React, { useCallback, useEffect, useMemo, useState } from "react";
import { CaretDown, Check } from "@phosphor-icons/react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { cn } from "@/lib/utils";
import { api } from "@/lib/api";

const triggerClass =
  "flex h-9 w-full items-center justify-between rounded-sm border border-zinc-200 bg-white px-3 text-sm text-zinc-950 focus:outline-none focus:ring-1 focus:ring-zinc-900 focus:border-zinc-900 disabled:cursor-not-allowed disabled:opacity-50";

function useDebouncedValue(value, delayMs = 300) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}

export function MerchantSelect({
  label = "Merchant",
  value = "",
  onChange,
  testId = "merchant-select",
  allowAll = true,
  allLabel = "All merchants",
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedLabel, setSelectedLabel] = useState("");
  const debouncedSearch = useDebouncedValue(search);

  const loadMerchants = useCallback(async (q) => {
    setLoading(true);
    try {
      const r = await api.get("/admin/merchants", {
        params: { q: q || undefined, limit: 50, page: 1 },
      });
      setItems(r.data.items ?? []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    loadMerchants(debouncedSearch);
  }, [open, debouncedSearch, loadMerchants]);

  useEffect(() => {
    if (!value) {
      setSelectedLabel("");
      return;
    }
    const hit = items.find((m) => m.id === value);
    if (hit) {
      setSelectedLabel(hit.company_name);
      return;
    }
    let cancelled = false;
    api.get(`/admin/merchants/${value}`)
      .then((r) => {
        if (!cancelled) setSelectedLabel(r.data.company_name || value);
      })
      .catch(() => {
        if (!cancelled) setSelectedLabel(value);
      });
    return () => { cancelled = true; };
  }, [value, items]);

  const display = useMemo(() => {
    if (!value) return allowAll ? allLabel : "Select merchant…";
    return selectedLabel || "Loading…";
  }, [value, allowAll, allLabel, selectedLabel]);

  return (
    <div>
      <label className="block text-[10px] uppercase tracking-[0.05em] font-semibold text-zinc-700 mb-1.5">
        {label}
      </label>
      <Popover open={open} onOpenChange={(next) => {
        setOpen(next);
        if (!next) setSearch("");
      }}
      >
        <PopoverTrigger asChild>
          <button
            type="button"
            role="combobox"
            aria-expanded={open}
            disabled={disabled}
            data-testid={testId}
            className={triggerClass}
          >
            <span className={cn("truncate text-left", !value && allowAll && "text-zinc-950")}>
              {display}
            </span>
            <CaretDown size={14} className="shrink-0 text-zinc-400" aria-hidden />
          </button>
        </PopoverTrigger>
        <PopoverContent
          className="w-[var(--radix-popover-trigger-width)] p-0 bg-white border-zinc-200 text-zinc-950 shadow-lg"
          align="start"
        >
          <Command shouldFilter={false} className="bg-white">
            <CommandInput
              placeholder="Search by name or email…"
              className="h-10 text-sm"
              value={search}
              onValueChange={setSearch}
            />
            <CommandList>
              {!loading && items.length === 0 && !allowAll && (
                <CommandEmpty className="text-zinc-500 text-sm py-6 text-center">
                  No merchants found.
                </CommandEmpty>
              )}
              {!loading && items.length === 0 && allowAll && debouncedSearch && (
                <div className="text-zinc-500 text-sm py-3 px-3 text-center">No merchants match your search.</div>
              )}
              <CommandGroup>
                {allowAll && (
                  <CommandItem
                    value="__all__"
                    className="text-sm data-[selected=true]:bg-zinc-100 data-[selected=true]:text-zinc-950"
                    onSelect={() => {
                      onChange("");
                      setOpen(false);
                      setSearch("");
                    }}
                  >
                    <Check
                      size={14}
                      className={cn("mr-2 shrink-0 text-zinc-950", !value ? "opacity-100" : "opacity-0")}
                    />
                    <span className="text-zinc-600">{allLabel}</span>
                  </CommandItem>
                )}
                {items.map((m) => (
                  <CommandItem
                    key={m.id}
                    value={`${m.company_name} ${m.email}`}
                    className="text-sm data-[selected=true]:bg-zinc-100 data-[selected=true]:text-zinc-950"
                    onSelect={() => {
                      onChange(m.id);
                      setSelectedLabel(m.company_name);
                      setOpen(false);
                      setSearch("");
                    }}
                  >
                    <Check
                      size={14}
                      className={cn(
                        "mr-2 shrink-0 text-zinc-950",
                        value === m.id ? "opacity-100" : "opacity-0",
                      )}
                    />
                    <span className="flex-1 truncate">{m.company_name}</span>
                    <span className="ml-2 text-xs text-zinc-400 truncate max-w-[40%]">{m.email}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
              {loading && (
                <div className="text-zinc-500 text-sm py-3 text-center">Searching…</div>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
